import { downloadResponse } from '@va/http-client';
import { useCallback } from 'react';
import zipcelx, { ZipCelXCell, ZipCelXDataSet } from 'zipcelx';
import { useAddNotification } from './useAddNotification';
import { useVerboseFilename } from './useVerboseFilename';

export type TabularExportCell = string | number | null | undefined;

const toCsvValue = (value: TabularExportCell) => {
  const str = String(value ?? '');
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const toXlsxCell = (value: TabularExportCell): ZipCelXCell =>
  typeof value === 'number' ? { value, type: 'number' } : { value: value ?? '', type: 'string' };

export const useTabularDataExport = (fileName: string, getRows: () => TabularExportCell[][] | undefined) => {
  const { showErrorNotification } = useAddNotification();
  const { createVerboseFilename } = useVerboseFilename();

  const downloadCSV = useCallback(async () => {
    const rows = getRows();
    if (!rows) return;

    const csv = rows.map((row) => row.map(toCsvValue).join(',')).join('\n');

    try {
      await downloadResponse(csv, createVerboseFilename({ fileName, extension: 'csv' }));
    } catch (error) {
      console.error(error);
      showErrorNotification();
    }
  }, [createVerboseFilename, fileName, getRows, showErrorNotification]);

  const downloadXLSX = useCallback(() => {
    const rows = getRows();
    if (!rows) return;

    const data: ZipCelXDataSet = rows.map((row) => row.map(toXlsxCell));

    try {
      zipcelx({ filename: createVerboseFilename({ fileName }), sheet: { data } });
    } catch (error) {
      console.error(error);
      showErrorNotification();
    }
  }, [createVerboseFilename, fileName, getRows, showErrorNotification]);

  return { downloadCSV, downloadXLSX };
};
